import { EntityType } from '@/models/EntityTypes';
import { Program } from '@/models/Program';
import { ProgramClass } from '@/models/ProgramClass';
import { Status } from '@/models/Status';
import { programService } from '@/services/ProgramService';
import { getFormattedExpirationDate, getFormatTimeHourMin, getPluralization, isExpireDateViewByStatus } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React from 'react';
import { useSelector } from 'react-redux';
import FavoriteSection from '../FavoriteSection';
import './style.scss';

interface Props {
    program: Program;
    classEnrollmentStatus: string;
    programClass?: ProgramClass;
    disabled: boolean;
}

const ProgramCard: React.FC<Props> = (props: Props) => {
    const { program, classEnrollmentStatus, programClass, disabled } = props;

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const totalCourses: number = (program?.courseAmount || 0) + (program?.workshopAmount || 0);

    const ratingValidation = () => {
        return program?.rating?.toFixed(1) || '-';
    };

    const getNameClassExpired = (): string => {
        return disabled ? 'card-program__expired' : '';
    };

    const getNameClassAlertTitle = (): string => {
        return programClass?.isExpired || (!!programClass && programClass.daysToExpire <= 30) ? 'card-program__alert-title' : '';
    };

    const getNameClassExpiredBackground = (): string => {
        return disabled ? 'card-program__expired-background' : '';
    };

    return (
        <div className="main">
            <div className={`card-program ${getNameClassExpiredBackground()}`}>
                <div className="card-program__body">
                    <div className="card-program__container-flag">
                        <div className={`card-program__label-program ${getNameClassExpired()}`}>{strings.PROGRAM}</div>

                        {programClass && programClass?.finishDate && isExpireDateViewByStatus(classEnrollmentStatus as Status) && (
                            <div className={`card-program__expiration-date ${getNameClassAlertTitle()}`}>
                                {getFormattedExpirationDate(programClass?.finishDate, programClass?.daysToExpire, programClass?.isExpired, false)}
                            </div>
                        )}
                    </div>
                    <div className={`d-flex flex-column ${getNameClassExpired()}`}>
                        <span className="card-program__title-program" title={program.name}>
                            {program.name}
                        </span>
                    </div>
                </div>

                <div className="card-program__thumb">
                    {!disabled && (
                        <FavoriteSection
                            buttonMode
                            customActiveClass="active"
                            customClass="card-list__like"
                            entityId={program.id as number}
                            entityName={program.name}
                            entityType={EntityType.PROGRAM}
                            moduleId={program.moduleId as number}
                        />
                    )}

                    <div
                        className={`card-program__thumb__img ${getNameClassExpired()}`}
                        style={{ backgroundImage: `url('${encodeURIComponent(program.mainImage)}')` }}
                    />
                    {!disabled && (
                        <div className="card-program__status-program">{programService.getProgramStatusLabel(classEnrollmentStatus, strings)}</div>
                    )}
                </div>
                <div className={`card-program__footer ${getNameClassExpired()}`}>
                    <div className="card-program__footer__info">
                        <div className="card-program__author" title={program.author}>
                            {program.author}
                        </div>
                        <div className="d-flex">
                            <div className="card-program__lesson">{getPluralization(totalCourses || 0, strings.COURSE, strings.COURSES)}</div>
                            <div className="card-program__duration">{getFormatTimeHourMin(program.workload || 0)}</div>
                            <div className="card-program__rating">
                                <i className="material-icons">grade</i>
                                {ratingValidation()}
                            </div>
                        </div>
                    </div>
                </div>

                {programClass?.program?.workshopAmount === 0 ? (
                    <div className={`card-program__progressBarContainer ${getNameClassExpired()}`}>
                        <div
                            className="card-program__progressBarContainer__progressBar"
                            style={{
                                width: programService.getProgramProgressPercentage(totalCourses || 0, program.totalCompletedCourses || 0) + '%',
                            }}
                        ></div>
                    </div>
                ) : (
                    <div className="card-program__workshopProgressBarContainer" />
                )}
            </div>
        </div>
    );
};

export default ProgramCard;
